import React, { ChangeEvent, useCallback, useState } from 'react';

import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';

import { InclusionFilters } from '~/components/Cruises/context/useCruiseInclusions';
import { TYPES } from '~/components/Cruises/pages/Inclusions/components/InclusionForm';
import { InclusionType } from '~/components/Cruises/types';

const ALL_INCLUSION_TYPES = 'ALL';

type InclusionTypeFilter = InclusionType | typeof ALL_INCLUSION_TYPES;

type LocalFormValues = {
  name: string;
  rateCodes: string;
  vendorCodes: string;
  departures: string;
  type: InclusionTypeFilter;
};

type Props = {
  isLoading: boolean;
  filters: InclusionFilters;
  onSearch: (filters: InclusionFilters) => void;
};

function filtersToLocal(filters: InclusionFilters): LocalFormValues {
  return {
    name: filters.name || '',
    rateCodes: filters.rateCodes?.join(', ') || '',
    vendorCodes: filters.vendorCodes?.join(', ') || '',
    departures: filters.departures?.join(', ') || '',
    type: filters.type || ALL_INCLUSION_TYPES,
  };
}

function parseCommaSeparated(value: string): string[] | undefined {
  return value
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    : undefined;
}

export default function InclusionSearchForm({ isLoading, filters, onSearch }: Props) {
  const [localValues, setLocalValues] = useState<LocalFormValues>(() => filtersToLocal(filters));

  const handleChangeField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.name) {
      setLocalValues((prev) => ({
        ...prev,
        [event.target.name]: event.target.value,
      }));
    }
  }, []);

  const handleSearch = useCallback(() => {
    onSearch({
      name: localValues.name || undefined,
      rateCodes: parseCommaSeparated(localValues.rateCodes),
      vendorCodes: parseCommaSeparated(localValues.vendorCodes),
      departures: parseCommaSeparated(localValues.departures),
      type: localValues.type === ALL_INCLUSION_TYPES ? undefined : localValues.type,
    });
  }, [localValues, onSearch]);

  return (
    <Stack direction="column" spacing={2} border={1} borderColor="divider" borderRadius={1} p={2}>
      <FormControl>
        <FormLabel>Inclusion Type</FormLabel>
        <RadioGroup row name="type" value={localValues.type || ALL_INCLUSION_TYPES} onChange={handleChangeField}>
          <FormControlLabel value={ALL_INCLUSION_TYPES} control={<Radio />} label="All" />
          <FormControlLabel value={TYPES.STANDARD} control={<Radio />} label="Standard" />
          <FormControlLabel value={TYPES.LUX_PLUS} control={<Radio />} label="Lux Plus" />
          <FormControlLabel value={TYPES.EVERGREEN} control={<Radio />} label="Evergreen" />
        </RadioGroup>
      </FormControl>

      <Stack direction="row" spacing={2}>
        <Box flexGrow={2}>
          <TextField
            fullWidth
            name="name"
            label="Inclusion Name"
            onChange={handleChangeField}
            value={localValues.name}
          />
        </Box>
      </Stack>

      <Stack direction="row" spacing={2}>
        <Box flexGrow={2}>
          <TextField
            fullWidth
            name="rateCodes"
            label="Rate IDs"
            onChange={handleChangeField}
            value={localValues.rateCodes}
            placeholder="Use comma to search multiple rate IDs"
          />
        </Box>
        <Box flexGrow={2}>
          <TextField
            fullWidth
            name="departures"
            label="Departure IDs"
            onChange={handleChangeField}
            value={localValues.departures}
            placeholder="Use comma to search multiple departures"
          />
        </Box>
        <Box flexGrow={1}>
          <TextField
            fullWidth
            name="vendorCodes"
            label="Vendor Code"
            onChange={handleChangeField}
            value={localValues.vendorCodes}
          />
        </Box>

        <Box>
          <Button variant="contained" disabled={isLoading} onClick={handleSearch}>
            Search Content
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
}
